import { t } from '../i18n';
// src/pages/AdBannerManagement.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdList from '../components/ads/AdList';
import AdPreview from '../components/ads/AdPreview';
import AdEditorModal from '../components/ads/AdEditorModal';
import { createAd, deleteAd, DEVICES, listAds, makeEmptyAd, reorderAds, sortAds, updateAd, } from '../components/ads/adsStorage';
export default function AdBannerManagement() {
    const [area, setArea] = useState('main'); // main | payment
    const [items, setItems] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [query, setQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [deviceFilter, setDeviceFilter] = useState('all');
    const [countryFilter, setCountryFilter] = useState('all');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modal, setModal] = useState({ open: false, mode: 'create', draft: null });
    const me = useMemo(() => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return { writer: user.nickname || user.id || 'admin' };
    }, []);
    const refresh = useCallback(async (preferredId = null) => {
        setLoading(true);
        try {
            const data = await listAds();
            const next = sortAds(data);
            setItems(next);
            setSelectedId((prev) => {
                if (preferredId && next.some((x) => x.id === preferredId))
                    return preferredId;
                if (prev && next.some((x) => x.id === prev))
                    return prev;
                const firstInArea = next.find((x) => x.area === area);
                return firstInArea?.id || next[0]?.id || null;
            });
        }
        catch (err) {
            alert(err.message || t("\uAD11\uACE0 \uBAA9\uB85D\uC744 \uBD88\uB7EC\uC624\uC9C0 \uBABB\uD588\uC5B4."));
        }
        finally {
            setLoading(false);
        }
    }, [area]);
    useEffect(() => {
        refresh();
    }, [refresh]);
    const countryOptions = useMemo(() => {
        const set = new Set(items
            .filter((x) => x.area === area)
            .map((x) => String(x.country || 'ALL').toUpperCase()));
        return ['all', ...Array.from(set).sort()];
    }, [items, area]);
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return sortAds(items.filter((ad) => {
            if (ad.area !== area)
                return false;
            if (statusFilter !== 'all' && ad._runtimeStatus !== statusFilter)
                return false;
            if (deviceFilter !== 'all' && ad.device !== deviceFilter)
                return false;
            if (countryFilter !== 'all' && ad.country !== countryFilter)
                return false;
            if (!q)
                return true;
            const hay = `${ad.label || ''}\n${ad.clickUrl || ''}\n${ad.country || ''}\n${ad.device || ''}`.toLowerCase();
            return hay.includes(q);
        }));
    }, [items, area, query, statusFilter, deviceFilter, countryFilter]);
    useEffect(() => {
        if (!filtered.some((x) => x.id === selectedId)) {
            setSelectedId(filtered[0]?.id || null);
        }
    }, [filtered, selectedId]);
    const selected = useMemo(() => filtered.find((x) => x.id === selectedId) || null, [filtered, selectedId]);
    const openCreate = () => {
        const draft = makeEmptyAd({ area, writer: me.writer });
        setModal({ open: true, mode: 'create', draft });
    };
    const openEdit = (ad) => {
        setModal({ open: true, mode: 'edit', draft: { ...ad } });
    };
    const closeModal = () => {
        if (saving)
            return;
        setModal({ open: false, mode: 'create', draft: null });
    };
    const onSave = async (form, file, options = {}) => {
        setSaving(true);
        try {
            let saved = null;
            if (modal.mode === 'create') {
                saved = await createAd({
                    ...form,
                    writer: me.writer,
                }, file, options);
            }
            else {
                saved = await updateAd(form.id, {
                    ...form,
                    writer: me.writer,
                }, file, options);
            }
            closeModal();
            await refresh(saved?.id || null);
        }
        catch (err) {
            alert(err.message || t("\uAD11\uACE0 \uC800\uC7A5\uC5D0 \uC2E4\uD328\uD588\uC5B4."));
        }
        finally {
            setSaving(false);
        }
    };
    const onDelete = async (ad) => {
        const ok = window.confirm(t('광고를 삭제할까?\n\n- {label}', { label: ad.label || t('(이름없음)') }));
        if (!ok)
            return;
        try {
            await deleteAd(ad.id);
            await refresh();
        }
        catch (err) {
            alert(err.message || t("\uAD11\uACE0 \uC0AD\uC81C\uC5D0 \uC2E4\uD328\uD588\uC5B4."));
        }
    };
    const move = async (id, dir) => {
        const current = items.find((x) => x.id === id);
        if (!current)
            return;
        const siblings = sortAds(items.filter((x) => x.area === current.area &&
            x.device === current.device &&
            x.country === current.country));
        const idx = siblings.findIndex((x) => x.id === id);
        if (idx < 0)
            return;
        const target = idx + dir;
        if (target < 0 || target >= siblings.length)
            return;
        const reordered = [...siblings];
        const temp = reordered[idx];
        reordered[idx] = reordered[target];
        reordered[target] = temp;
        const payload = reordered.map((item, index) => ({
            id: item.id,
            order: (index + 1) * 10,
        }));
        try {
            await reorderAds(payload);
            await refresh(id);
        }
        catch (err) {
            alert(err.message || t("\uC815\uB82C \uBCC0\uACBD\uC5D0 \uC2E4\uD328\uD588\uC5B4."));
        }
    };
    const onAreaChange = (nextArea) => {
        setArea(nextArea);
        setQuery('');
        setStatusFilter('all');
        setCountryFilter('all');
    };
    return (<div className="min-h-[calc(100vh-120px)]">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t("\uAD11\uACE0 \uC774\uBBF8\uC9C0 \uB4F1\uB85D/\uAD00\uB9AC")}</h1>
          <p className="text-sm text-gray-500">
            {t('Responsive 광고는 한 건에서 Desktop / Mobile 이미지를 함께 관리 · 기존 전용 광고도 호환')}
          </p>
          <p className="text-xs text-gray-400 mt-1">{t("\uC815\uB82C \uD654\uC0B4\uD45C\uB294 \uAC19\uC740 area/device/country \uBB36\uC74C \uC548\uC5D0\uC11C\uB9CC \uB3D9\uC791\uD574.")}</p>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <div className="text-xs text-gray-500 mb-1">{t("Device \uD544\uD130")}</div>
            <select value={deviceFilter} onChange={(e) => setDeviceFilter(e.target.value)} className="px-3 py-2 rounded border border-gray-200 bg-white">
              <option value="all">{t("\uC804\uCCB4")}</option>
              {DEVICES.map((item) => (<option key={item.value} value={item.value}>
                  {item.label}
                </option>))}
            </select>
          </div>

          <div>
            <div className="text-xs text-gray-500 mb-1">{t("Country \uD544\uD130")}</div>
            <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)} className="px-3 py-2 rounded border border-gray-200 bg-white">
              {countryOptions.map((country) => (<option key={country} value={country}>
                  {country === 'all' ? t("\uC804\uCCB4") : country}
                </option>))}
            </select>
          </div>

          <button onClick={openCreate} className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-black">{t("+ \uC0C8 \uAD11\uACE0 \uB4F1\uB85D")}</button>
        </div>
      </div>

      <div className="mb-4 inline-flex max-w-full rounded-lg border border-gray-200 overflow-hidden bg-white shadow-sm">
        <button onClick={() => onAreaChange('main')} className={[
            'px-4 py-2 text-sm',
            area === 'main' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50',
        ].join(' ')}>{t("\uBA54\uC778 \uC601\uC5ED")}</button>
        <button onClick={() => onAreaChange('payment')} className={[
            'px-4 py-2 text-sm',
            area === 'payment' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50',
        ].join(' ')}>{t("\uACB0\uC81C \uC601\uC5ED")}</button>
      </div>

      <div className="mb-3 text-xs text-gray-400">{loading ? t("\uAD11\uACE0 \uBAA9\uB85D \uBD88\uB7EC\uC624\uB294 \uC911...") : ' '}</div>

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-stretch lg:items-start min-w-0">
        <div className="w-full lg:w-[420px] lg:shrink-0 min-w-0">
          <AdList items={filtered} selectedId={selectedId} query={query} onQueryChange={setQuery} statusFilter={statusFilter} onStatusFilterChange={setStatusFilter} onSelect={setSelectedId} onCreate={openCreate} onEdit={openEdit} onDelete={onDelete} onMoveUp={(id) => move(id, -1)} onMoveDown={(id) => move(id, +1)}/>
        </div>

        <div className="flex-1 min-w-0">
          <AdPreview ad={selected} onEdit={() => selected && openEdit(selected)} onDelete={() => selected && onDelete(selected)}/>
        </div>
      </div>

      <AdEditorModal open={modal.open} mode={modal.mode} area={area} draft={modal.draft} saving={saving} onClose={closeModal} onSave={onSave}/>
    </div>);
}
